// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ProductGridContainer, { ProductGridContainerStyle } from "@conklin/App/Components/Product/ProductGridContainer";
import ProductPriceAndActions, {
    ProductPriceAndActionsStyle,
} from "@conklin/App/Components/Product/ProductPriceAndActions";
import { getSettingsCollection } from "@conklin/App/Store/Selectors/SettingsSelectors";
import { ProductListProductInformationStyles } from "@conklin/Overrides/Widgets/ProductList/ProductListProductInformation";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import { ProductCardSelections } from "@insite/content-library/Widgets/ProductList/ProductCardSelections";
import ProductListActions from "@insite/content-library/Widgets/ProductList/ProductListActions";
import ProductListProductImage, {
    ProductListProductImageStyles,
} from "@insite/content-library/Widgets/ProductList/ProductListProductImage";
import ProductListProductInformation from "@insite/content-library/Widgets/ProductList/ProductListProductInformation";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

type Props = ProductCardSelections & HasProduct;

export interface ProductListProductGridCardStyles {
    wrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    sponsoredText?: TypographyPresentationProps;
    // XNG-Changes
    productGridContainerStyles?: ProductGridContainerStyle;
    productListImageStyles?: ProductListProductImageStyles;
    productInformationStyles?: ProductListProductInformationStyles;
    productPriceAndActionsStyles?: ProductPriceAndActionsStyle;
    productListDefaultImageStyles?: ProductListProductImageStyles;
}

export const productGridCardStyles: ProductListProductGridCardStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            height: 100%;
            overflow: hidden;
            padding-bottom: 10px;
        `,
    },
    imageWrapper: {
        css: css`
            margin: auto;
            padding: 10px 50px;
        `,
    },
    sponsoredText: {
        css: css`
            font-size: 0.9em;
            opacity: 0.5;
            position: absolute;
            top: 12px;
        `,
    },
};

const styles = productGridCardStyles;

const ProductListProductGridCard: FC<Props> = ({
    product,
    showImage,
    showCompare,
    showAttributes,
    showAvailability,
    showBrand,
    showPartNumbers,
    showTitle,
    showAddToList,
    showPrice,
    showImageBadges,
    maximumNumberOfImageBadges,
    showTextBadges,
    maximumNumberOfTextBadges,
}) => {
    // XNG-Changes
    const notFoundImagePath = useAppSelector(state => {
        const settingsCollection = getSettingsCollection(state) as any;
        return (
            settingsCollection.catalogSettings?.notFoundSmallImagePath ||
            settingsCollection.catalogSettings?.notFoundMediumImagePath ||
            settingsCollection.catalogSettings?.notFoundLargeImagePath
        );
    });
    // XNG-Changes
    const productWithDefaultImage =
        (!product.smallImagePath && !product.mediumImagePath) ||
        product.smallImagePath === notFoundImagePath ||
        (!product.smallImagePath && product.mediumImagePath === notFoundImagePath);

    return (
        <ProductGridContainer
            data-test-selector={`productListProductCard${product.id}`}
            extendedStyles={styles.productGridContainerStyles}
        >
            <ProductGridContainer.Image>
                {product.isSponsored && <Typography {...styles.sponsoredText}>{translate("Sponsored")}</Typography>}
                <ProductListProductImage
                    showImage={showImage}
                    showCompare={false}
                    showImageBadge={showImageBadges}
                    showTextBadge={showTextBadges}
                    // XNG-Changes
                    extendedStyles={
                        productWithDefaultImage ? styles.productListDefaultImageStyles : styles.productListImageStyles
                    }
                />
            </ProductGridContainer.Image>
            <ProductGridContainer.Info>
                <ProductListProductInformation
                    showAttributes={showAttributes}
                    showAvailability={false}
                    showBrand={showBrand}
                    showPartNumbers={showPartNumbers}
                    showTitle={showTitle}
                    showImageBadges={showImageBadges}
                    maxImageBadges={maximumNumberOfImageBadges}
                    showTextBadges={showTextBadges}
                    maxTextBadges={maximumNumberOfTextBadges}
                    // XNG-Change
                    {...{ extendedStyles: styles.productInformationStyles, showPrice: false }}
                />
            </ProductGridContainer.Info>
            <ProductGridContainer.PriceAndActions>
                <ProductPriceAndActions
                    showCompare={showCompare}
                    showAddToList={showAddToList}
                    showPrice={showPrice}
                    showAvailability={showAvailability && !product.isVariantParent}
                    showAddToCartActions={true}
                    extendedStyles={styles.productPriceAndActionsStyles}
                    containerClassName="pricing-info-container"
                />
            </ProductGridContainer.PriceAndActions>
        </ProductGridContainer>
    );
};

export default withProduct(ProductListProductGridCard);
